import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, valuesOf } from '../skillUsage';

export const zoomThroughTransitionSkill: CodeoSkill = {
    name: SKILL_NAMES.zoomThroughTransition,
    kind: 'codeo',
    description:
        'Fly the frame into an element until it fills and passes the camera, arriving in the next ' +
        'state — the punch-through, for moving inside a thing rather than cutting away from it.',
    documentUsesSkill: check((script) => valuesOf(script, 'scale').some((value) => value >= 4)),
    instructions: `# Zoom-Through Transition

The camera accelerates into one element — a shape, an icon, a letter's counter, a window — until it fills the frame and passes, and the next state is already there behind it. It reads as travelling *into* something, which makes it the strongest way to say the second state is inside, beneath, or a consequence of the first.

It is the most aggressive phase change in the library and it needs a genuine aperture to pass through: something with a hole, a dark centre, or an edge the camera can plausibly cross.

## Markup

Two states stacked, with the outgoing one carrying the aperture.

\`\`\`html
<div id="zoom-stage" class="absolute inset-0 overflow-hidden flex items-center justify-center">
  <div id="arrival" class="flex items-center justify-center" style="position: absolute; inset: 0; background: var(--l5-color-1, #101820); font-family: var(--l5-font-0), sans-serif; font-size: 128px; font-weight: 800; color: var(--l5-color-0, #ffffff);">Inside every plan</div>
  <div id="departure" class="flex items-center justify-center" style="position: absolute; inset: 0; background: var(--l5-color-0, #f4f6f9);">
    <div id="aperture" style="width: 300px; height: 300px; border-radius: 50%; background: var(--l5-color-1, #101820)"></div>
  </div>
</div>
\`\`\`

## Timeline code

Scale the departing state hugely from an origin centred on the aperture. The arrival is revealed simply by the departure leaving the frame.

**The wait before the punch is part of the shot.** The timeline runs in the scene's own seconds, so a first tween at 1.2s leaves the opening 1.2s a still image. Give the departure state its own entrance and a slow creep toward the aperture, so the punch arrives as an acceleration of something already moving.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const PUNCH = 1.2;
// The punch has to drive toward the aperture's centre, so it tracks however the aperture is
// placed — here the layout centres it, which is why this is the stage's centre too.
const ORIGIN = '50% 50%';

gsap.set('#departure', { scale: 1, transformOrigin: ORIGIN });
gsap.set('#arrival', { scale: 1.14, opacity: 1 });
gsap.set('#aperture', { scale: 0.82, opacity: 0 });

// The departure state establishes itself and starts drifting in before the punch,
// so nothing in the scene is ever simply held.
tl.to('#aperture', { scale: 1, opacity: 1, duration: 0.6, ease: 'back.out(1.3)' }, 0.1);
tl.to('#departure', { scale: 1.06, duration: PUNCH - 0.1, ease: 'power1.in' }, 0.1);

tl.to('#departure', { scale: 14, duration: 0.8, ease: 'power3.in' }, PUNCH);
tl.to('#departure', { opacity: 0, duration: 0.18, ease: 'none' }, PUNCH + 0.62);
// The arrival settles back from a slight push, so the camera reads as decelerating on the far side.
tl.to('#arrival', { scale: 1, duration: 0.9, ease: 'power2.out' }, PUNCH + 0.55);

window.__timelines = { main: tl };
\`\`\`

The pre-roll ends at exactly \`PUNCH\`, so \`power1.in\` hands straight into \`power3.in\` and the whole approach reads as one continuous acceleration rather than a creep followed by a separate lunge. That acceleration is what makes it feel like travel rather than a scale tween.

## Adapt

- **Aperture**: any dark or solid shape works, but a real hole — the counter of an "O", a circle in a logo, a doorway — is far stronger because the geometry justifies the pass-through.
- **Punch depth**: scale to 12–18×. Under 8× the aperture never fully covers the frame and the seam shows.
- **Deceleration**: the arrival's 1.14 → 1.0 settle is what makes the far side feel like a landing. Without it the transition stops dead.
- **Motion blur**: a short blur ramp on the departing state during the fastest part sells the speed — see \`${SKILL_NAMES.softFocusResolve}\` for the filter handling.
- **Reverse**: shrinking the arrival away into the aperture reads as retreating out of something, which suits an ending.
- **Match the colours**: if the aperture and the arrival's background are the same colour, the crossover is seamless and the cut becomes invisible.

## Pitfalls

- **Don't leave the opening held.** Whatever seconds pass before your first tween are seconds of still image — a punch at 1.0s freezes the opening second, and on a short scene that is most of it. Keep the first move early, and judge it against the scene's own length.
- Set \`transformOrigin\` to the aperture's actual centre as a percentage of the stage. Any offset and the camera flies past the hole instead of through it.
- The departing state is \`scale\`-tweened, so it must not carry a CSS \`transform\`.
- At 14× the departing state's other content is enormously magnified during the punch — keep the outgoing composition simple, or the frames mid-transition are a mess of giant fragments.
- Fade the departure only at the very end, once the aperture already covers the frame. Fading early exposes the seam.
- This is a hard, loud transition. One per video at most, and never between two states that are merely adjacent — it has to mean "inside".
- Elements magnified past the frame need \`data-l5-allow-offscreen\`.
- Don't also cut the content inside the arrival at the same moment. Let the camera land before anything else starts — see \`${SKILL_NAMES.velocityMatchedCut}\` for the continuous-motion alternative.`,
};
